import { all, put, select, takeEvery } from '@redux-saga/core/effects';

import { actions } from '../../actions';
import { describeApiError } from './apiError';

const _identifiers = {
    dataset: 'ds-tasks',
    annotation: 'annotations',
    annotation_review: 'annotations/review',
    annotation_details: 'annotations/detail',
    information: 'information',
    project: 'projects/',
    project_legend: 'projects_legends/',
    project_market: 'projects_market/',
    workflow_instructions: 'workflow/instructions',
    workflow_registries: 'workflow/registries',
    workflow_guidelines: 'workflow/guidelines',
    workflow_batches: 'workflow/batches',
    workflow_rounds: 'workflow/rounds',
    workflow_annotation_tasks: 'workflow/annotation-tasks',
};

function* notifRemoveFailure(action) {
    const { endpoint, id, error } = action.payload;
    if (endpoint.includes(_identifiers.dataset))
        yield put(
            actions.notification.add.failure(`Unable to remove the snapshot ${id}:
        ${error?.response?.data?.detail || error?.response?.data} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.annotation)) {
        yield put(
            actions.notification.add.failure(`Unable to remove the annotation ${id}:
        ${error?.response?.data?.detail || error?.response?.data} (Code ${error?.response?.status})`),
        );
    }
    if (endpoint.includes(_identifiers.workflow_instructions)) {
        const detail = error?.response?.data?.detail || error?.response?.data || 'Unknown error';
        yield put(
            actions.notification.add.failure(`Unable to delete the instruction:
        ${detail} (Code ${error?.response?.status})`),
        );
    }
    if (endpoint.includes(_identifiers.workflow_annotation_tasks)) {
        const detail = error?.response?.data?.detail || error?.response?.data || 'Unknown error';
        yield put(
            actions.notification.add.failure(`Unable to delete the annotation task:
        ${detail} (Code ${error?.response?.status})`),
        );
    }
}

function* notifRemoveSuccess(action) {
    const { endpoint, id } = action.payload;
    if (endpoint.includes(_identifiers.dataset))
        yield put(actions.notification.add.success(`The snapshot ${id} has been removed`));
    if (endpoint.includes(_identifiers.annotation))
        yield put(actions.notification.add.success(`The annotation ${id} has been removed`));
    if (endpoint.includes(_identifiers.workflow_instructions))
        yield put(actions.notification.add.success(`Instruction has been deleted successfully.`));
    if (endpoint.includes(_identifiers.workflow_annotation_tasks))
        yield put(actions.notification.add.success(`Annotation task has been deleted successfully.`));
}

function* notifPutSuccess(action) {
    const { endpoint, id, response } = action.payload;
    if (endpoint.includes(_identifiers.annotation_review))
        if (id === undefined) yield put(actions.notification.add.success(response?.data?.detail));
        else yield put(actions.notification.add.success(`The annotation ${id} has been reviewed.`));
    else if (endpoint.includes(_identifiers.annotation))
        yield put(actions.notification.add.success(`The annotation ${id} has been restored`));
    // Removed success notifications for "updated successfully" messages
    // else if (endpoint.includes(_identifiers.workflow_instructions))
    //     yield put(actions.notification.add.success(`Instruction updated successfully.`));
    // else if (endpoint.includes(_identifiers.workflow_rounds))
    //     yield put(actions.notification.add.success(`Round updated successfully.`));
    // else if (endpoint.includes(_identifiers.workflow_annotation_tasks))
    //     yield put(actions.notification.add.success(`Annotation task updated successfully.`));
}

function* notifPutFailure(action) {
    const { endpoint, id, error } = action.payload;
    if (endpoint.includes(_identifiers.annotation_review))
        if (id === undefined)
            yield put(
                actions.notification.add.failure(`Something went wrong while reviewing:
        ${error?.response?.data?.detail} (Code ${error?.response?.status})`),
            );
        else
            yield put(
                actions.notification.add.failure(`Unable to review the annotation ${id}:
        ${error?.response?.data?.detail} (Code ${error?.response?.status})`),
            );
    else if (endpoint.includes(_identifiers.annotation))
        yield put(
            actions.notification.add.failure(`Unable to restore the annotation ${id}:
        ${error?.response?.data?.detail || error?.response?.data} (Code ${error?.response?.status})`),
        );
    else if (endpoint.includes(_identifiers.project)) {
        const errorMessages = error?.response?.data?.detail;
        yield put(
            actions.notification.add.failure(`Failed to update the project:
        ${errorMessages} (Code ${error?.response?.status})`),
        );
    } else if (endpoint.includes(_identifiers.workflow_instructions)) {
        const detail = error?.response?.data?.detail || error?.response?.data;
        yield put(
            actions.notification.add.failure(`Unable to update the instruction:
        ${detail} (Code ${error?.response?.status})`),
        );
    } else if (endpoint.includes(_identifiers.workflow_rounds)) {
        // Round validation errors are field-keyed (e.g. {cat_projects: [...]}),
        // so they need flattening rather than a bare `detail` lookup.
        yield put(
            actions.notification.add.failure(`Unable to update the round:
        ${describeApiError(error)} (Code ${error?.response?.status})`),
        );
    } else if (endpoint.includes(_identifiers.workflow_annotation_tasks)) {
        const detail = error?.response?.data?.detail || error?.response?.data;
        yield put(
            actions.notification.add.failure(`Unable to update the annotation task:
        ${detail} (Code ${error?.response?.status})`),
        );
    }
}

function* notifPostSuccess(action) {
    const { endpoint } = action.payload;
    // Get response from state if available
    const state = yield select((state) => state.data[endpoint]);
    const response = state?.response || action.payload.response;

    if (endpoint.includes(_identifiers.information))
        yield put(actions.notification.add.success(`The latest information has been saved.`));
    if (endpoint.includes(_identifiers.project_legend))
        yield put(actions.notification.add.success(`The legend has been updated.`));
    if (endpoint.includes(_identifiers.project_market))
        yield put(actions.notification.add.success(`The market share has been updated.`));
    if (endpoint.includes(_identifiers.workflow_instructions))
        yield put(actions.notification.add.success(`Instruction created successfully.`));
    if (endpoint.includes(_identifiers.workflow_registries))
        yield put(actions.notification.add.success(`Registry created successfully.`));
    if (endpoint.includes(_identifiers.workflow_guidelines))
        yield put(actions.notification.add.success(`Guideline created successfully.`));
    if (endpoint.includes(_identifiers.workflow_batches))
        yield put(actions.notification.add.success(`Batch created successfully.`));
    if (endpoint.includes(_identifiers.workflow_rounds)) {
        // Check if this is a mark-done action (endpoint contains /mark-done/)
        if (endpoint.includes('/mark-done/')) {
            const roundData = state?.data || response?.data;
            const batchMarkedInactive = roundData?.batch_marked_inactive;
            if (batchMarkedInactive) {
                yield put(
                    actions.notification.add.success(
                        `Round marked as done successfully. All rounds in the batch are now complete, and the batch has been marked as inactive.`,
                    ),
                );
            } else {
                yield put(actions.notification.add.success(`Round marked as done successfully.`));
            }
        }
        // Check if this is an apply action (endpoint contains /apply/)
        else if (endpoint.includes('/apply/')) {
            const roundData = state?.data || response?.data;
            if (roundData?.task_id) {
                // Processing the round...
                return;
            }

            const applySummary = roundData?.apply_summary || response?.data?.apply_summary;
            if (applySummary) {
                const { total_tasks, successful_updates, failed_updates, errors } = applySummary;
                if (failed_updates > 0) {
                    let message = `Round applied with issues:\n`;
                    message += `• ${successful_updates}/${total_tasks} jobs updated successfully\n`;
                    message += `• ${failed_updates} job(s) failed`;
                    if (errors && errors.length > 0) {
                        message += `\n\nErrors:\n`;
                        errors.slice(0, 5).forEach((err) => {
                            message += `• Job ${err.job_id} (${err.pid}): ${err.error}\n`;
                        });
                        if (errors.length > 5) {
                            message += `... and ${errors.length - 5} more error(s)`;
                        }
                    }
                    yield put(actions.notification.add.warning(message));
                } else {
                    yield put(
                        actions.notification.add.success(
                            `Round applied successfully. All ${total_tasks} job(s) updated.`,
                        ),
                    );
                }
            } else {
                yield put(actions.notification.add.success(`Round applied successfully.`));
            }
        } else if (endpoint.endsWith('/workflow/rounds/')) {
            yield put(actions.notification.add.success(`Round created successfully.`));
        }
        // Assign users to round
        else if (endpoint.includes('/assign-users/')) {
            const responseData = state?.data || response?.data;
            const updatedCount = Number(responseData?.updated_count || 0);
            const message =
                responseData?.message ||
                (updatedCount > 0
                    ? `Assigned annotators/reviewers for ${updatedCount} task(s).`
                    : 'Assignments are already up to date for this round.');
            yield put(actions.notification.add.success(message));
        }
    }
    if (endpoint.includes(_identifiers.workflow_annotation_tasks) && endpoint.includes('/verify/')) {
        const taskData = state?.data || response?.data;
        const taskPid = taskData?.task?.pid || taskData?.pid || 'task';
        yield put(actions.notification.add.success(`Task ${taskPid} has been verified successfully.`));
    }
    if (endpoint.includes(_identifiers.workflow_annotation_tasks) && endpoint.includes('/import-to-mimir/')) {
        const taskData = state?.data || response?.data;
        const taskPid = taskData?.task?.pid || taskData?.pid || 'task';
        const jobId = taskData?.task?.cat_job_id || taskData?.cat_job_id;
        const message =
            jobId != null && jobId !== ''
                ? `Task ${taskPid} (Job ${jobId}) import has been successfully started. Check workflow events for progress.`
                : `Task ${taskPid} import has been successfully started. Check workflow events for progress.`;
        yield put(actions.notification.add.success(message));
    }
}

function* notifPostFailure(action) {
    const { endpoint, error } = action.payload;
    if (endpoint.includes(_identifiers.information))
        yield put(
            actions.notification.add.failure(`Unable to save the latest information:
        ${error?.response?.data?.detail} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.project)) {
        const errorMessages = error?.response?.data?.detail;
        yield put(
            actions.notification.add.failure(`Unable to create the project:
        ${errorMessages} (Code ${error?.response?.status})`),
        );
    }
    if (endpoint.includes(_identifiers.project_legend))
        yield put(
            actions.notification.add.failure(`Unable to update the legend:
        ${error?.response?.data?.detail} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.project_market))
        yield put(
            actions.notification.add.failure(`Unable to update the market share:
        ${error?.response?.data?.detail} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.workflow_instructions))
        yield put(
            actions.notification.add.failure(`Unable to create the instruction:
        ${error?.response?.data?.detail || error?.response?.data} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.workflow_registries))
        yield put(
            actions.notification.add.failure(`Unable to create the registry:
        ${error?.response?.data?.detail || error?.response?.data} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.workflow_guidelines))
        yield put(
            actions.notification.add.failure(`Unable to create the guideline:
        ${error?.response?.data?.detail || error?.response?.data} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.workflow_batches))
        // Batch create can fail with a field-keyed body (e.g. {cat_projects: [...]}
        // when an External Round Manager is assigned without an allow-list), so it
        // needs flattening or the toast reads "[object Object]".
        yield put(
            actions.notification.add.failure(`Unable to create the batch:
        ${describeApiError(error)} (Code ${error?.response?.status})`),
        );
    if (endpoint.includes(_identifiers.workflow_rounds)) {
        // Field-keyed validation bodies (e.g. {cat_projects: [...]}) must be
        // flattened, or the toast reads "[object Object]".
        const detail = describeApiError(error);
        // Check if this is a mark-done action (endpoint contains /mark-done/)
        if (endpoint.includes('/mark-done/')) {
            yield put(
                actions.notification.add.failure(
                    `Unable to mark round as done:\n${detail} (Code ${error?.response?.status})`,
                ),
            );
        }
        // Check if this is an apply action (endpoint contains /apply/)
        else if (endpoint.includes('/apply/')) {
            yield put(
                actions.notification.add.failure(
                    `Unable to apply the round:\n${detail} (Code ${error?.response?.status})`,
                ),
            );
        }
        // Force-refresh failure
        else if (endpoint.includes('/force-refresh-status/')) {
            yield put(
                actions.notification.add.failure(
                    `Unable to start status refresh:\n${detail} (Code ${error?.response?.status})`,
                ),
            );
        }
        // Assign users failure
        else if (endpoint.includes('/assign-users/')) {
            yield put(
                actions.notification.add.failure(
                    `Could not assign users for round:\n${detail} (Code ${error?.response?.status})`,
                ),
            );
        } else {
            yield put(
                actions.notification.add.failure(`Unable to create the round:
        ${detail} (Code ${error?.response?.status})`),
            );
        }
    }
    if (endpoint.includes(_identifiers.workflow_annotation_tasks)) {
        if (endpoint.includes('/import-to-mimir/')) {
            const detail = error?.response?.data?.detail || error?.response?.data || 'Unknown error';
            yield put(
                actions.notification.add.failure(
                    `Unable to start task import:\n${detail} (Code ${error?.response?.status})`,
                ),
            );
        } else if (endpoint.includes('/verify/')) {
            const detail = error?.response?.data?.detail || error?.response?.data || 'Unknown error';
            yield put(
                actions.notification.add.failure(
                    `Unable to verify the annotation task:\n${detail} (Code ${error?.response?.status})`,
                ),
            );
        } else {
            // Regular create action
            let errorMessage = error?.response?.data?.detail || error?.response?.data;

            // Handle non_field_errors (like unique constraint violations)
            if (error?.response?.data?.non_field_errors && Array.isArray(error.response.data.non_field_errors)) {
                errorMessage = error.response.data.non_field_errors.join('\n');
                // Make the error message more user-friendly
                if (errorMessage.includes('must make a unique set')) {
                    errorMessage =
                        'A task with this combination of Round, PID, and CAT Job ID already exists.\n\nPlease use a different PID or CAT Job ID, or check if the task already exists in this round.';
                }
            } else if (typeof errorMessage === 'object') {
                // If errorMessage is an object, try to stringify it nicely
                if (errorMessage.non_field_errors && Array.isArray(errorMessage.non_field_errors)) {
                    errorMessage = errorMessage.non_field_errors.join('\n');
                    // Make the error message more user-friendly
                    if (errorMessage.includes('must make a unique set')) {
                        errorMessage =
                            'A task with this combination of Round, PID, and CAT Job ID already exists.\n\nPlease use a different PID or CAT Job ID, or check if the task already exists in this round.';
                    }
                } else {
                    errorMessage = JSON.stringify(errorMessage);
                }
            }

            yield put(
                actions.notification.add.failure(
                    `Unable to create the annotation task:\n${errorMessage} (Code ${error?.response?.status})`,
                ),
            );
        }
    }
}

function* notifFetchFailure(action) {
    const { endpoint, error } = action.payload;
    if (endpoint.includes(_identifiers.annotation_details))
        yield put(
            actions.notification.add.failure(`Unable to fetch the annotation:
        ${error?.response?.data?.detail || error?.response?.data} (Code ${error?.response?.status})`),
        );
}

export default function* watchNotifData() {
    yield all([
        yield takeEvery(actions.api.data.fetch.failure.toString(), notifFetchFailure),
        yield takeEvery(actions.api.data.remove.failure.toString(), notifRemoveFailure),
        yield takeEvery(actions.api.data.remove.success.toString(), notifRemoveSuccess),
        yield takeEvery(actions.api.data.put.failure.toString(), notifPutFailure),
        yield takeEvery(actions.api.data.put.success.toString(), notifPutSuccess),
        yield takeEvery(actions.api.data.post.failure.toString(), notifPostFailure),
        yield takeEvery(actions.api.data.post.success.toString(), notifPostSuccess),
    ]);
}
